/**
 * Optional password gate shown before the game boots.
 *
 * Active only when a `password` environment variable is set at build/dev time
 * (baked into the client by Vite `define` as `__GAME_PASSWORD__`). When no
 * password is configured the gate is a no-op and the game starts immediately.
 *
 * Note: this is a SOFT gate. The game is a static client-side bundle, so the
 * configured password is embedded in the shipped JavaScript and can be read by
 * anyone who inspects it. It keeps casual visitors out; it is not real security.
 */

const SESSION_KEY = "codeandco.unlocked";

/** Runs `start` immediately if no password is configured (or already unlocked
 *  this session); otherwise shows the gate and runs `start` once unlocked. */
export function runWithPasswordGate(start: () => void): void {
  const password = __GAME_PASSWORD__;

  if (!password || sessionGet(SESSION_KEY) === "1") {
    start();
    return;
  }

  injectStyleOnce();

  const overlay = document.createElement("div");
  overlay.className = "pw-gate";
  overlay.innerHTML = `
    <form class="pw-card" novalidate>
      <div class="pw-title">Code &amp; Co</div>
      <div class="pw-sub">Enter the password to play.</div>
      <input class="pw-input" type="password" autocomplete="current-password"
             aria-label="Password" spellcheck="false" />
      <button class="pw-btn" type="submit">Enter</button>
      <div class="pw-error" role="alert" hidden>Wrong password. Try again.</div>
    </form>
  `;
  document.body.appendChild(overlay);

  const form = overlay.querySelector<HTMLFormElement>(".pw-card")!;
  const input = overlay.querySelector<HTMLInputElement>(".pw-input")!;
  const card = form;
  const error = overlay.querySelector<HTMLDivElement>(".pw-error")!;

  input.focus();

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    if (input.value.trim() === password) {
      sessionSet(SESSION_KEY, "1");
      overlay.remove();
      start();
      return;
    }
    error.hidden = false;
    input.select();
    // Retrigger the shake animation.
    card.classList.remove("pw-shake");
    void card.offsetWidth;
    card.classList.add("pw-shake");
  });
}

function injectStyleOnce(): void {
  if (document.getElementById("pw-gate-style")) return;
  const style = document.createElement("style");
  style.id = "pw-gate-style";
  style.textContent = `
    .pw-gate {
      position: fixed; inset: 0; z-index: 9999;
      display: flex; align-items: center; justify-content: center;
      background: #0e0f13;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    }
    .pw-card {
      display: flex; flex-direction: column; gap: 14px;
      width: min(360px, 82vw); padding: 28px;
      background: #1a1d24; border: 1px solid #2c313c; border-radius: 10px;
      box-shadow: 0 0 40px rgba(0, 0, 0, 0.6);
    }
    .pw-title { color: #4fa3ff; font-size: 22px; font-weight: 700; letter-spacing: 0.5px; }
    .pw-sub { color: #9aa0ac; font-size: 13px; margin-top: -6px; }
    .pw-input {
      background: #0e0f13; color: #e6e6e6;
      border: 1px solid #2c313c; border-radius: 6px;
      padding: 10px 12px; font: inherit; font-size: 15px; outline: none;
    }
    .pw-input:focus { border-color: #4fa3ff; }
    .pw-btn {
      background: #4fa3ff; color: #0e0f13;
      border: none; border-radius: 6px; padding: 10px 12px;
      font: inherit; font-size: 15px; font-weight: 700; cursor: pointer;
    }
    .pw-btn:hover { filter: brightness(1.08); }
    .pw-error { color: #d14b4b; font-size: 13px; min-height: 1em; }
    .pw-shake { animation: pw-shake 0.32s ease; }
    @keyframes pw-shake {
      0%, 100% { transform: translateX(0); }
      20%, 60% { transform: translateX(-7px); }
      40%, 80% { transform: translateX(7px); }
    }
  `;
  document.head.appendChild(style);
}

function sessionGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function sessionSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode, blocked) - gate just re-prompts */
  }
}
